// Per-dog coat-condition log. Quick check-ins that live outside a full
// grooming session — spotting matting between baths, tracking ear/nail
// state, watching how the coat responds season over season.
import { useCallback, useEffect, useState } from "react";
import { format, parseISO } from "date-fns";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { SectionTitle } from "@/components/app/Bits";
import { Trash2, Plus, Sparkles } from "lucide-react";

const COAT_STATES = ["healthy", "dry", "oily", "matted", "shedding"];
const EAR_OPTIONS = [
  { value: "good", label: "Clean" },
  { value: "waxy", label: "Waxy" },
  { value: "redness", label: "Red / irritated" },
  { value: "other", label: "Other — see notes" },
];
const NAIL_OPTIONS = [
  { value: "good", label: "Trim OK" },
  { value: "needs_trim", label: "Needs a trim" },
  { value: "split_broken", label: "Split / broken" },
];

const blank = (dogId) => ({
  dog_id: dogId,
  log_date: new Date().toISOString().slice(0, 10),
  coat_state: [],
  skin_issues: "",
  ears: "good",
  nails: "good",
  notes: "",
});

export default function CoatConditionSection({ dog }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(blank(dog.id));
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/grooming/conditions", { params: { dog_id: dog.id } });
      setItems(data.items || []);
    } catch {
      setItems([]);
    }
  }, [dog.id]);

  useEffect(() => { load(); setForm(blank(dog.id)); }, [dog.id, load]);

  const toggleCoat = (state) => {
    setForm((f) => {
      const has = f.coat_state.includes(state);
      return { ...f, coat_state: has ? f.coat_state.filter((s) => s !== state) : [...f.coat_state, state] };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.log_date) return;
    setSaving(true);
    try {
      await api.post("/grooming/conditions", form);
      toast.success("Condition logged");
      setForm(blank(dog.id));
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/grooming/conditions/${id}`);
      await load();
    } catch {
      toast.error("Could not delete");
    }
  };

  const earLabel = (v) => EAR_OPTIONS.find((o) => o.value === v)?.label || v;
  const nailLabel = (v) => NAIL_OPTIONS.find((o) => o.value === v)?.label || v;

  return (
    <div className="space-y-4" data-testid="coat-condition-section">
      <SectionTitle overline="Between grooms" title="Coat condition log" />
      <p className="text-sm text-muted-foreground max-w-2xl -mt-2">
        Quick check-ins to catch drift early — a mat starting, an ear that looks warm, a nail worth
        clipping before it splits.
      </p>

      <Card className="border-border shadow-none rounded-lg">
        <CardContent className="p-5 sm:p-6">
          <form onSubmit={submit} className="space-y-4" data-testid="coat-condition-form">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Date</Label>
                <Input
                  type="date"
                  value={form.log_date}
                  onChange={(e) => setForm({ ...form, log_date: e.target.value })}
                  className="mt-1.5"
                  required
                  data-testid="coat-log-date"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Ears</Label>
                <select
                  value={form.ears}
                  onChange={(e) => setForm({ ...form, ears: e.target.value })}
                  className="mt-1.5 w-full border border-input rounded-md bg-card px-3 py-2 text-sm"
                  data-testid="coat-log-ears"
                >
                  {EAR_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Nails</Label>
                <select
                  value={form.nails}
                  onChange={(e) => setForm({ ...form, nails: e.target.value })}
                  className="mt-1.5 w-full border border-input rounded-md bg-card px-3 py-2 text-sm"
                  data-testid="coat-log-nails"
                >
                  {NAIL_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Coat state (pick any)</Label>
              <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="coat-state-toggles">
                {COAT_STATES.map((s) => {
                  const active = form.coat_state.includes(s);
                  return (
                    <button
                      key={s}
                      type="button"
                      onClick={() => toggleCoat(s)}
                      className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                        active
                          ? "bg-primary text-primary-foreground border-primary"
                          : "bg-card text-foreground/85 border-border hover:bg-secondary/50"
                      }`}
                      data-testid={`coat-state-${s}`}
                    >
                      {s.charAt(0).toUpperCase() + s.slice(1)}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Skin issues (optional)</Label>
              <Input
                value={form.skin_issues}
                onChange={(e) => setForm({ ...form, skin_issues: e.target.value })}
                placeholder='e.g. "hot spot behind left elbow", "dandruff along spine"'
                className="mt-1.5"
                data-testid="coat-log-skin"
              />
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Notes</Label>
              <Textarea
                rows={2}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Coat feel, tangles noticed, plans for next groom…"
                className="mt-1.5 text-sm"
                data-testid="coat-log-notes"
              />
            </div>

            <Button
              type="submit"
              disabled={saving}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
              data-testid="coat-condition-save"
            >
              <Plus size={14} className="mr-1.5" /> {saving ? "Saving…" : "Log check-in"}
            </Button>
          </form>
        </CardContent>
      </Card>

      {items.length === 0 ? (
        <Card className="border-dashed border-border shadow-none rounded-lg">
          <CardContent className="p-6 text-sm text-muted-foreground text-center">
            No check-ins yet.
          </CardContent>
        </Card>
      ) : (
        <ul className="space-y-2" data-testid="coat-condition-list">
          {items.map((it) => (
            <Card
              key={it.id}
              as="li"
              className="border-border shadow-none rounded-lg"
              data-testid={`coat-condition-item-${it.id}`}
            >
              <CardContent className="p-4 sm:p-5">
                <div className="flex items-start gap-3">
                  <div className="h-8 w-8 rounded-md bg-secondary text-secondary-foreground flex items-center justify-center shrink-0">
                    <Sparkles size={14} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
                      {it.log_date ? format(parseISO(it.log_date), "MMM d, yyyy") : "—"}
                    </p>
                    <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs">
                      {(it.coat_state || []).map((s) => (
                        <span key={s} className="bg-primary/10 text-primary rounded-full px-2 py-0.5">
                          {s}
                        </span>
                      ))}
                      <span className="text-muted-foreground">· Ears: {earLabel(it.ears)}</span>
                      <span className="text-muted-foreground">· Nails: {nailLabel(it.nails)}</span>
                    </div>
                    {it.skin_issues ? (
                      <p className="mt-1 text-sm text-foreground/85"><strong>Skin:</strong> {it.skin_issues}</p>
                    ) : null}
                    {it.notes ? (
                      <p className="mt-1 text-sm text-foreground/80 whitespace-pre-line">{it.notes}</p>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    onClick={() => remove(it.id)}
                    className="text-muted-foreground hover:text-destructive p-1 shrink-0"
                    title="Remove"
                    data-testid={`coat-condition-remove-${it.id}`}
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </CardContent>
            </Card>
          ))}
        </ul>
      )}
    </div>
  );
}
